import { jwtDecode, type JwtPayload } from 'jwt-decode'

export interface Claims extends JwtPayload {
  issuer?: string
  email_hash?: string
  company_divvy_uuid?: string
  company_divvy_uuids?: string[]
  credit_application_id?: string
  credit_application_ids?: string[]
  jwt_lookup_id?: string
  type?: string
  session_id?: string
}

/**
 * Decodes a JWT token and returns the map of claims.
 *
 * @param token - The JWT token to decode.
 * @returns Claims - The map of claims from the given token.
 */
export const getDecodedToken = (token: string): Claims => {
  return jwtDecode<Claims>(token)
}

export const getSessionIdFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)
  return decodedToken?.session_id
}

export const getCompanyUuidFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)
  return decodedToken?.company_divvy_uuid
}

export const getCompanyUuidsFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)
  return decodedToken?.company_divvy_uuids
}

export const getCreditAppIdFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)

  return decodedToken?.credit_application_id
}

export const getCreditAppIdsFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)

  return decodedToken?.credit_application_ids
}

export const getJwtLookupIdFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)

  return decodedToken?.jwt_lookup_id
}

export const getExpirationFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)

  return decodedToken?.exp
}

export const getEmailHashFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)

  return decodedToken?.email_hash
}

export const getFormattedExpirationDateFromToken = (token: string | null) => {
  const expiration = getExpirationFromToken(token)

  if (!expiration) return null

  const date = new Date(0)
  date.setUTCSeconds(expiration)
  return date
}

export const getTypeFromToken = (token: string | null) => {
  if (!token) return null
  const decodedToken = getDecodedToken(token)
  return decodedToken?.type
}

export const isAccessToken = (token: string | null) => {
  if (!token) {
    return false
  }

  return getTypeFromToken(token) === 'access_token'
}
